import React, { useCallback, useMemo, useRef } from "react";
import {
  type AgGrid,
  Banner,
  Button,
  Card,
  EmptyState,
  Flex,
  Grid,
  LoadingIndicator,
  Text,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { docTypeLabel } from "#utils/classificationGrouping";
import * as styles from "./styles_vanilla.css";
import type { PackageDocument } from "#viewModelTypes";
import type { ApolloError } from "@apollo/client";

export type PackageDocumentsTabProps = {
  documents: readonly PackageDocument[];
  loading: boolean;
  error?: ApolloError;
  /** Opens the PDF viewer for the chosen document. */
  onSelectDocument: (document: PackageDocument) => void;
  /** Opens the remove-confirm modal for the chosen document. */
  onRemoveDocument: (document: PackageDocument) => void;
  /** Downloads the original uploaded file for a document. */
  onDownloadDocument: (document: PackageDocument) => void;
  /** documentId whose download is in flight, so its row shows a busy state. */
  downloadingDocumentId?: string | null;
};

// Compact grid sizing, matching the doc-ingestion design's Documents table.
const HEADER_HEIGHT = 36;
const ROW_HEIGHT = 44;
const TYPE_COLUMN_WIDTH = 200;
const DOWNLOAD_COLUMN_WIDTH = 56;
const DOWNLOAD_COLUMN_ID = "download";
const REMOVE_COLUMN_WIDTH = 64;
const REMOVE_COLUMN_ID = "remove";
/** Keeps the Name column wide enough to hold a full filename before
 *  sizeColumnsToFit grows it into the space the fixed columns leave. */
const NAME_COLUMN_MIN_WIDTH = 240;

/** AG Grid row: the bound document plus its display type label, so the Type
 *  column can bind a plain field rather than compute in a value getter. */
type DocRow = PackageDocument & { typeLabel: string };

/** Grid context carrying the source documents and remove handler to the
 *  module-level cell renderer, so the renderer stays a stable component (not
 *  redefined every render). */
type DocGridContext = {
  documents: readonly PackageDocument[];
  onRemoveDocument: (document: PackageDocument) => void;
};

/** Remove-action cell. Module-level so AG Grid sees a stable component type; the
 *  source documents and remove handler arrive via grid `context`, and the
 *  original PackageDocument (not the DocRow, which carries the internal
 *  `typeLabel`) is handed back to keep the callback contract. */
function RemoveDocumentCellRenderer(
  params: AgGrid.ICellRendererParams<DocRow, unknown, DocGridContext>,
) {
  const rowDocument =
    params.data && params.context.documents.find((d) => d.id === params.data?.id);
  if (!rowDocument) {
    return null;
  }
  return (
    <Button
      variant="muted"
      icon="trash"
      aria-label={`Remove ${rowDocument.filename}`}
      onClick={(event) => {
        // Row click opens the PDF viewer; the remove action must not.
        event.stopPropagation();
        params.context.onRemoveDocument(rowDocument);
      }}
    />
  );
}

/** Extra params the download column passes its cell renderer via
 *  `cellRendererParams` (kept a stable module-level component so the grid
 *  doesn't remount it every render). */
type DownloadCellProps = AgGrid.ICellRendererParams<DocRow> & {
  documents: readonly PackageDocument[];
  downloadingDocumentId?: string | null;
  onDownloadDocument: (document: PackageDocument) => void;
};

const DownloadCell: React.FC<DownloadCellProps> = ({
  data,
  documents,
  downloadingDocumentId,
  onDownloadDocument,
}) => {
  if (!data) {
    return null;
  }
  if (downloadingDocumentId === data.documentId) {
    return <LoadingIndicator size="small" />;
  }
  return (
    <Button
      variant="muted"
      icon="download"
      aria-label={`Download ${data.filename}`}
      onClick={() => {
        // Hand back the original PackageDocument (not the DocRow, which
        // carries the internal typeLabel), matching onRowClicked.
        const clicked = documents.find((document) => document.id === data.id);
        if (clicked) {
          onDownloadDocument(clicked);
        }
      }}
    />
  );
};

/**
 * PackageDocumentsTab — the package-detail Documents tab. Renders the package's
 * bound documents as a Name/Type grid inside a "Documents" card, matching the
 * doc-ingestion design. Row click opens the PDF viewer. Presentational: it takes
 * the already-mapped view-model and owns no data fetching. Each row carries a
 * download action whose URL fetch is owned by the parent and a Remove action
 * that opens the danger-confirm modal owned by the slideout; per MVP scope the
 * design's Upload action is still omitted.
 */
export const PackageDocumentsTab: React.FC<PackageDocumentsTabProps> = ({
  documents,
  loading,
  error,
  onSelectDocument,
  onRemoveDocument,
  onDownloadDocument,
  downloadingDocumentId,
}) => {
  const rowData = useMemo<DocRow[]>(
    () =>
      documents.map((document) => ({
        ...document,
        typeLabel: docTypeLabel(document.documentType) ?? "—",
      })),
    [documents],
  );

  const columnDefs = useMemo<AgGrid.ColDef<DocRow>[]>(
    () => [
      {
        field: "filename",
        headerName: "Name",
        // Grows via sizeColumnsToFit to fill the width left by the fixed Type
        // column, so the row spans the whole grid (full-width row borders).
        minWidth: NAME_COLUMN_MIN_WIDTH,
      },
      {
        field: "typeLabel",
        headerName: "Type",
        width: TYPE_COLUMN_WIDTH,
        suppressMovable: true,
        suppressSizeToFit: true,
      },
      {
        colId: DOWNLOAD_COLUMN_ID,
        headerName: "",
        width: DOWNLOAD_COLUMN_WIDTH,
        suppressMovable: true,
        suppressSizeToFit: true,
        cellClass: styles.actionCell,
        cellRenderer: DownloadCell,
        cellRendererParams: {
          documents,
          downloadingDocumentId,
          onDownloadDocument,
        },
      },
      {
        colId: REMOVE_COLUMN_ID,
        headerName: "",
        width: REMOVE_COLUMN_WIDTH,
        suppressMovable: true,
        suppressSizeToFit: true,
        cellClass: styles.actionCell,
        cellRenderer: RemoveDocumentCellRenderer,
      },
    ],
    [documents, onDownloadDocument, downloadingDocumentId],
  );

  const gridContext = useMemo<DocGridContext>(
    () => ({ documents, onRemoveDocument }),
    [documents, onRemoveDocument],
  );

  const defaultColDef = useMemo<AgGrid.ColDef<DocRow>>(
    () => ({ sortable: false, resizable: false, suppressHeaderMenuButton: true }),
    [],
  );

  // Fit columns to the grid width so Name fills the space beside the fixed Type
  // column and each row's border spans the full table, not just the cells.
  const onGridReady = useCallback((event: AgGrid.GridReadyEvent<DocRow>) => {
    event.api.sizeColumnsToFit();
  }, []);
  const onGridSizeChanged = useCallback((event: AgGrid.GridSizeChangedEvent<DocRow>) => {
    event.api.sizeColumnsToFit();
  }, []);

  // Cell clicks fire before the row click below (both from the same native
  // click), so record here which column this click landed in and let the row
  // handler bail for the action columns. Sourced from AG Grid's own column
  // bookkeeping (`colDef`/`column`) rather than inspecting `event.event.target`
  // via DOM traversal — the raw target's ancestor chain proved unreliable at
  // the exact moment this fires (e.g. `closest("[col-id]")` came back empty
  // even for a direct click on the button in production), so this avoids
  // depending on the live DOM shape entirely.
  const lastClickedColIdRef = useRef<string | null>(null);
  const onCellClicked = useCallback((event: AgGrid.CellClickedEvent<DocRow>) => {
    lastClickedColIdRef.current = event.column.getColId();
  }, []);

  const onRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<DocRow>) => {
      // A click on a per-row action control (download or remove) must not also
      // open the viewer; each button fires its own handler. AG Grid's row-click
      // listener is native, so a React stopPropagation on the button wouldn't
      // reach it — instead the cell-click handler above records which column was
      // hit and this guard bails for the action columns.
      if (
        lastClickedColIdRef.current === DOWNLOAD_COLUMN_ID ||
        lastClickedColIdRef.current === REMOVE_COLUMN_ID
      ) {
        return;
      }
      // Hand back the original document (not the DocRow, which carries the
      // internal `typeLabel`) so the callback contract stays a PackageDocument.
      const clicked = event.data && documents.find((d) => d.id === event.data?.id);
      if (clicked) {
        onSelectDocument(clicked);
      }
    },
    [documents, onSelectDocument],
  );

  if (loading && documents.length === 0) {
    return (
      <Flex alignItems="center" gap={Sizes.SM}>
        <LoadingIndicator size="small" />
        <Text variant="secondary-md" asSpan>
          Loading documents…
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Banner variant="danger" title="Couldn’t load documents" description={error.message} />
    );
  }

  if (documents.length === 0) {
    // Also the empty-package surface: a package whose documents were all
    // removed lands here; upload is the recovery path.
    return (
      <EmptyState
        illustration="searching"
        title="No documents"
        description="This package has no documents. Upload a document to add it to this package."
      />
    );
  }

  return (
    <Card title="Documents" variant="white" padding="xs" isFullWidth>
      <Grid<DocRow>
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        context={gridContext}
        onGridReady={onGridReady}
        onGridSizeChanged={onGridSizeChanged}
        onCellClicked={onCellClicked}
        onRowClicked={onRowClicked}
        rowClass={styles.clickableRow}
        domLayout="autoHeight"
        suppressBrowserResizeObserver
        suppressCellFocus
        headerHeight={HEADER_HEIGHT}
        rowHeight={ROW_HEIGHT}
        animateRows={false}
      />
    </Card>
  );
};
